import { useEffect, useRef, useState } from 'react';
import {
  Button, Checkbox, DrawerBody, DrawerHeader, DrawerHeaderTitle, Input, MessageBar, MessageBarBody,
  OverlayDrawer, Spinner, Tab, TabList, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import type { EntityDiscoveryItem, EntityPreviewInspection, EntitySchemaInspection } from '../../api/types';
import type { RelatedEntity } from './entityRelationships';
import { relationshipJoinLabel } from './entityRelationships';

type InspectorTab = 'relationships' | 'preview' | 'schema';
type LoadState<T> = { status: 'idle' | 'loading' | 'error' | 'success'; data?: T; error?: string };

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'hidden' },
  stickyControls: {
    display: 'flex', flexDirection: 'column', flexShrink: 0, gap: '12px',
    paddingBottom: '12px', backgroundColor: tokens.colorNeutralBackground1,
  },
  relationshipList: { flex: 1, minHeight: 0, overflowY: 'auto' },
  relationship: {
    display: 'flex', alignItems: 'center', gap: '8px', minHeight: '68px', padding: '0 4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  relationshipSelected: { backgroundColor: tokens.colorNeutralBackground1Selected },
  relationshipContent: { flex: 1, minWidth: 0 },
  relationshipName: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  joinExpression: {
    display: 'block', color: tokens.colorNeutralForeground3, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingTop: '2px',
  },
  tabContent: { flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: '4px' },
  tableWrap: { overflowX: 'auto' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: tokens.fontSizeBase200 },
  cell: { padding: '6px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, textAlign: 'left', whiteSpace: 'nowrap' },
  empty: { color: tokens.colorNeutralForeground3, paddingTop: '12px' },
});

interface Props {
  connectionId: string;
  entity: EntityDiscoveryItem | null;
  relatedEntities: RelatedEntity[];
  selectedNames: Set<string>;
  onClose: () => void;
  onToggleSelection: (entity: EntityDiscoveryItem) => void;
}

export function TableInspectorDrawer({ connectionId, entity, relatedEntities, selectedNames, onClose, onToggleSelection }: Props) {
  const styles = useStyles();
  const [tab, setTab] = useState<InspectorTab>('relationships');
  const [schema, setSchema] = useState<LoadState<EntitySchemaInspection>>({ status: 'idle' });
  const [preview, setPreview] = useState<LoadState<EntityPreviewInspection>>({ status: 'idle' });
  const [relationshipSearch, setRelationshipSearch] = useState('');
  const [schemaSearch, setSchemaSearch] = useState('');
  const relationshipListRef = useRef<HTMLDivElement>(null);
  const inspectionIdentity = entity ? `${connectionId}:${entity.name}` : undefined;
  const inspectionIdentityRef = useRef(inspectionIdentity);
  inspectionIdentityRef.current = inspectionIdentity;

  useEffect(() => {
    setTab('relationships');
    setSchema({ status: 'idle' });
    setPreview({ status: 'idle' });
    setRelationshipSearch('');
    setSchemaSearch('');
    if (relationshipListRef.current) relationshipListRef.current.scrollTop = 0;
  }, [connectionId, entity?.name]);

  const relatedTableName = (related: RelatedEntity) => {
    const candidate = related.entity.source_schema === 'public' && related.entity.source_table
      ? related.entity.source_table
      : related.entity.label.replace(/^public\./, '');
    const ambiguous = relatedEntities.some(other => other.entity.name !== related.entity.name
      && (other.entity.source_table ?? other.entity.label.replace(/^public\./, '')) === candidate);
    return ambiguous ? related.entity.label : candidate;
  };

  const relationshipQuery = relationshipSearch.trim().toLowerCase();
  const filteredRelatedEntities = relationshipQuery ? relatedEntities.filter(related =>
    related.entity.name.toLowerCase().includes(relationshipQuery)
      || related.entity.label.toLowerCase().includes(relationshipQuery)
      || related.relationships.some(relationship =>
        relationshipJoinLabel(relationship, entity?.name ?? '').toLowerCase().includes(relationshipQuery)),
  ) : relatedEntities;
  const schemaQuery = schemaSearch.trim().toLowerCase();
  const filteredFields = schema.data?.fields.filter(field => !schemaQuery
    || field.name.toLowerCase().includes(schemaQuery)
    || field.label?.toLowerCase().includes(schemaQuery)
    || field.type.toLowerCase().includes(schemaQuery)
    || field.description?.toLowerCase().includes(schemaQuery)) ?? [];

  const loadSchema = async () => {
    if (!entity || schema.status === 'loading') return;
    const requestedEntity = entity.name;
    const requestedIdentity = inspectionIdentity;
    setSchema({ status: 'loading' });
    try {
      const data = await connectionsService.inspectEntitySchema(connectionId, requestedEntity);
      if (inspectionIdentityRef.current === requestedIdentity) setSchema({ status: 'success', data });
    } catch (error) {
      if (inspectionIdentityRef.current === requestedIdentity) setSchema({ status: 'error', error: error instanceof Error ? error.message : 'Schema inspection failed' });
    }
  };
  const loadPreview = async () => {
    if (!entity || preview.status === 'loading') return;
    const requestedEntity = entity.name;
    const requestedIdentity = inspectionIdentity;
    setPreview({ status: 'loading' });
    try {
      const data = await connectionsService.previewEntity(connectionId, requestedEntity);
      if (inspectionIdentityRef.current === requestedIdentity) setPreview({ status: 'success', data });
    } catch (error) {
      if (inspectionIdentityRef.current === requestedIdentity) setPreview({ status: 'error', error: error instanceof Error ? error.message : 'Preview failed' });
    }
  };

  useEffect(() => {
    if (tab === 'schema' && schema.status === 'idle') void loadSchema();
    if (tab === 'preview' && preview.status === 'idle') void loadPreview();
  }, [tab, connectionId, entity?.name]); // Requests intentionally start only when their tab is opened.

  const loading = (state: LoadState<unknown>) => state.status === 'loading' ? <Spinner size="small" /> : null;
  const failure = (state: LoadState<unknown>, retry: () => void) => state.status === 'error' ? (
    <MessageBar intent="error"><MessageBarBody>{state.error} <Button size="small" onClick={retry}>Retry</Button></MessageBarBody></MessageBar>
  ) : null;

  return (
    <OverlayDrawer position="end" size="medium" open={entity !== null} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DrawerHeader><DrawerHeaderTitle action={<Button appearance="subtle" icon={<DismissRegular />} aria-label="Close table inspector" onClick={onClose} />}>
        {entity?.label ?? 'Table inspector'}
      </DrawerHeaderTitle></DrawerHeader>
      <DrawerBody className={styles.body}>
        <div className={styles.stickyControls}>
          <TabList selectedValue={tab} onTabSelect={(_, data) => setTab(data.value as InspectorTab)}>
            <Tab value="relationships">Relationships</Tab><Tab value="preview">Preview</Tab><Tab value="schema">Schema</Tab>
          </TabList>
          {tab === 'relationships' && <Input aria-label="Search relationships" placeholder="Search related tables or fields" value={relationshipSearch}
            contentBefore={<SearchRegular />} onChange={(_, data) => setRelationshipSearch(data.value)} />}
          {tab === 'schema' && schema.data && <Input aria-label="Search schema" placeholder="Search fields or types" value={schemaSearch}
            contentBefore={<SearchRegular />} onChange={(_, data) => setSchemaSearch(data.value)} />}
        </div>
        {tab === 'relationships' && <div className={styles.relationshipList} ref={relationshipListRef}>
          {relatedEntities.length === 0
          ? <Text className={styles.empty}>No relationships were discovered for this table.</Text>
          : filteredRelatedEntities.length === 0
          ? <Text className={styles.empty}>No relationships match this search.</Text>
          : filteredRelatedEntities.map(related => <div
              className={`${styles.relationship} ${selectedNames.has(related.entity.name) ? styles.relationshipSelected : ''}`}
              key={related.entity.name}>
              <Checkbox
                  aria-label={`Select ${relatedTableName(related)}`}
                  checked={selectedNames.has(related.entity.name)}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                  onChange={() => onToggleSelection(related.entity)}
                />
              <div className={styles.relationshipContent}>
                <Text className={styles.relationshipName} weight="semibold">{relatedTableName(related)}</Text>
                <Text className={styles.joinExpression} size={200}>
                  {related.relationships.map(relationship => relationshipJoinLabel(relationship, entity!.name)).join(' · ')}
                </Text>
              </div>
            </div>)}</div>}
        {tab === 'schema' && <div className={styles.tabContent}>{loading(schema)}{failure(schema, () => void loadSchema())}{schema.data && <>
          {schema.data.fields.length === 0
            ? <Text className={styles.empty}>No schema fields are available.</Text>
            : filteredFields.length === 0
            ? <Text className={styles.empty}>No schema fields match this search.</Text>
            : <div className={styles.tableWrap}><table className={styles.table}>
          <thead><tr><th className={styles.cell}>Field</th><th className={styles.cell}>Type</th><th className={styles.cell}>Nullable</th></tr></thead>
          <tbody>{filteredFields.map(field => <tr key={field.name}><td className={styles.cell}>{field.label ?? field.name}{field.primary_key ? ' (PK)' : ''}</td><td className={styles.cell}>{field.type}</td><td className={styles.cell}>{field.nullable === true ? 'Yes' : field.nullable === false ? 'No' : 'Unknown'}</td></tr>)}</tbody>
        </table></div>}</>}</div>}
        {tab === 'preview' && <div className={styles.tabContent}>{loading(preview)}{failure(preview, () => void loadPreview())}{preview.data && <div className={styles.tableWrap}><table className={styles.table}>
          <thead><tr>{preview.data.columns.map(column => <th className={styles.cell} key={column}>{column}</th>)}</tr></thead>
          <tbody>{preview.data.rows.map((row, index) => <tr key={index}>{row.map((value, column) => <td className={styles.cell} key={column}>{value === null ? 'null' : String(value)}</td>)}</tr>)}</tbody>
        </table>{preview.data.rows.length === 0 && <Text className={styles.empty}>No rows available.</Text>}</div>}</div>}
      </DrawerBody>
    </OverlayDrawer>
  );
}
